import React from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { deleteCourse, resetActiveCourseData, setCourseList } from '@/lib/slices/course-slice';

import ConfirmationModal from '@/common/components/modals/general/ConfirmationModal';
import { DeleteIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import { CourseData } from 'course-types';

function DeleteCourseModal() {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const getSuccessMessage = (status: string): string => {
    return status === 'partial' ? 'Removed as course collaborator' : 'Successfully deleted course';
  };

  const handleDeleteCourse = async () => {
    const response = await (
      await Request.delete('course/removeCourse', { courseId: activeCourseData.id })
    )?.json();

    if (!response?.status) {
      throw new Error('Something went wrong with the deletion. Please refresh page.');
    }

    toast.success(getSuccessMessage(response.status));

    // Refresh user course cookie from server so all arrays (user/group/enterprise) are pruned
    await UserCourseStorageService.retrieveCourseCookie();

    // Sync Redux course list with updated enterprise list immediately
    const updatedCookie = UserCourseStorageService.getCourseCookie();
    if (updatedCookie) {
      dispatch(setCourseList(updatedCookie.all.enterprise ?? []));
    }

    // Remove course from current Redux list as a safety (in case view is still mounted)
    dispatch(deleteCourse(activeCourseData.id));

    // Clear active course and local cached course data
    dispatch(resetActiveCourseData());
    UserCourseStorageService.resetCourseData();
  };

  return (
    <ConfirmationModal
      modalName='DeleteCourse'
      title={`Delete "${activeCourseData.name}"`}
      subtitle='This action cannot be undone'
      message='Are you sure you want to delete this course?'
      description='Warning: if there are multiple course collaborators remaining, your account will be removed as a collaborator. The course will still exist for remaining collaborators.'
      confirmText='Delete Course'
      confirmIcon={<DeleteIcon className='w-5 h-5' />}
      isDestructive={true}
      onConfirm={handleDeleteCourse}
    />
  );
}

export default DeleteCourseModal;
